/**
 * REK-2008 (SSO.14) / REK-2053 / REK-2137: completion path for the login-box
 * "new user" pivot — BOTH halves of it.
 *
 * When the Lemonade LOGIN box discovers the typed email has no Clerk account
 * (`form_identifier_not_found`), it pivots in-place to a choice screen
 * (REK-2137): ONE combined 18+/ToS checkbox gates a primary "Sign up with
 * Google" button and a secondary "Create account & send code" email path.
 * THIS module finishes the account for both paths — only after the caller
 * supplies a real affirmation timestamp:
 *
 *   0. Reject empty / whitespace `termsAcceptedAtIso` — never invent a
 *      timestamp here (SSO.14 D-B / REK-2053). The UI gate lives on the
 *      pivot choice screen; this module owns the write-contract half.
 *   1. Implied cookie consent BEFORE the session activates (same ordering
 *      contract as useFastAuth.verifyOtp — PostHog must be opted-in before the
 *      provider's identify() fires). Email path: via attemptSignUpEmailCode's
 *      onBeforeSessionActivate. Google path: the /auth/sso-callback page
 *      grants it before completeSocialSsoCallback activates the session.
 *   2. Session grant + `waitForClerkSessionResolution` (the SSO.2
 *      clerk-webhook provisions the shadow auth.users/profiles rows; the
 *      provider polls for them for up to 90s).
 *   3. Post-verify persistence, mirroring the useFastAuth semantics where they
 *      apply to an account created WITHOUT the scan funnel:
 *        - `profiles.terms_accepted_at` (caller-supplied checkbox timestamp)
 *        - `terms_of_service` AND `age_verification` consents via the
 *          REK-1938 `record-consent` edge function (`grantConsent` —
 *          `user_consents` is service-role-only for writes; the combined
 *          checkbox covers both consents per the REK-1730 dual-consent
 *          precedent)
 *        - `process-referral` when a valid `reklaim_attribution.ref` exists
 *        - UTM + Meta click IDs from `reklaim_attribution` onto
 *          `profiles.signup_*` (REK-2146 — same columns as FastAuth OTP)
 *        - `email_verified` / `signup_completed` / `registration_completed`
 *          analytics (+ `consent_decision` when implied consent applied);
 *          origin/method report 'email' or `oauth_<provider>` per path;
 *          `signup_completed` carries `entry_source`; Marketing consent →
 *          `pushCompleteRegistration` (REK-2146)
 *
 * The Google path adds one wrinkle (REK-2137): OAuth is a full-page redirect,
 * so the checkbox affirmation is persisted across the round trip in
 * localStorage (`storeSsoTermsAcceptance` on click →
 * `consumeSsoTermsAcceptance` in the callback, which ALWAYS clears the flag
 * so it can never leak into a later, unaffirmed signup). The timestamp is
 * still the real checkbox ISO stamped by the pivot screen — never
 * synthesized here or there.
 *
 * Deliberately NOT done here (differences from the /signup scan funnel):
 *   - `onboarding_status` stays `pending` — the pivot routes the user to
 *     /onboarding (name + DOB gate + Ruby chat) instead of completing
 *     onboarding inline (D-A).
 *   - No breach_data insert and no signup points award — there was no scan.
 *   - No marketing-email consent — the pivot card has no marketing checkbox.
 *
 * Only reachable under VITE_AUTH_PROVIDER=clerk (the pivot stage never renders
 * on the legacy Supabase engine).
 */
import { supabase } from '@/integrations/supabase/client';
import { attemptSignUpEmailCode } from '@/lib/clerk-auth-flows';
import {
  waitForClerkSessionResolution,
  getClerkAuthUser,
  type ClerkSignInMethod,
} from '@/lib/clerk-token';
import {
  ACCOUNT_LOCKED_MESSAGE,
  SESSION_VERIFY_FAILED_MESSAGE,
} from '@/providers/auth-context';
import { analytics } from '@/lib/analytics';
import { EXPERIMENTS } from '@/lib/analytics/experiments';
import { pushCompleteRegistration } from '@/lib/analytics/gtm';
import {
  readStoredAttribution,
  resolveEntrySource,
  type ReklaimAttribution,
} from '@/lib/analytics/properties';
import { grantImpliedConsent } from '@/lib/cookieConsent';
import { grantConsent } from '@/lib/recordConsent';
import { safeError } from '@/lib/safeLog';

/** Active experiment assignments for W1-0 funnel props (mirrors useFastAuth). */
function getExperimentVariantsMap(): Record<string, string> {
  const variants: Record<string, string> = {};
  const cookieVariant = analytics.getFeatureFlag(EXPERIMENTS.COOKIE_CONSENT_VARIANT);
  if (typeof cookieVariant === 'string' && cookieVariant.length > 0) {
    variants[EXPERIMENTS.COOKIE_CONSENT_VARIANT] = cookieVariant;
  }
  return variants;
}

/**
 * Map `reklaim_attribution` → `profiles.signup_*` columns (REK-2146).
 * Pivot has no hook options — storage is the sole source (Auth/Home capture).
 */
function profileUpdatesFromStoredAttribution(
  attribution: ReklaimAttribution,
): Record<string, string> {
  const updates: Record<string, string> = {};
  if (attribution.source) updates.signup_source = attribution.source;
  if (attribution.medium) updates.signup_medium = attribution.medium;
  if (attribution.campaign) updates.signup_campaign = attribution.campaign;
  if (attribution.fbclid) updates.signup_fbclid = attribution.fbclid;
  if (attribution.campaign_id) updates.signup_campaign_id = attribution.campaign_id;
  if (attribution.adset_id) updates.signup_adset_id = attribution.adset_id;
  if (attribution.ad_id) updates.signup_ad_id = attribution.ad_id;
  return updates;
}

/**
 * Outlasts ClerkAuthProvider's 90s SSO.2 webhook-race polling window so the
 * provider always reports a terminal state before we give up (same margin as
 * useFastAuth's CLERK_SIGN_UP_RESOLUTION_TIMEOUT_MS).
 */
const PIVOT_RESOLUTION_TIMEOUT_MS = 100_000;

/**
 * Referral codes are `UPPER(SUBSTRING(MD5(user_id) FROM 1 FOR 8))` — exactly
 * 8 uppercase hex characters (handle_new_user trigger; mirrors the REK-1480
 * validation in LemonadeOnboarding).
 */
const REFERRAL_CODE_PATTERN = /^[0-9A-F]{8}$/;

/**
 * Read the referral code the /auth page stored from `?ref=` (or a prior
 * landing) in `reklaim_attribution`. Malformed codes are ignored — the pivot
 * card has no referral input, so there is nothing for the user to correct.
 */
export function readAttributionReferralCode(): string | null {
  try {
    const stored = localStorage.getItem('reklaim_attribution');
    if (!stored) return null;
    const parsed = JSON.parse(stored) as { ref?: unknown };
    const code =
      typeof parsed.ref === 'string' ? parsed.ref.trim().toUpperCase() : '';
    return REFERRAL_CODE_PATTERN.test(code) ? code : null;
  } catch {
    return null;
  }
}

export type PivotVerifyResult =
  | { status: 'complete' }
  | { status: 'error'; message: string; accountLocked: boolean };

export interface VerifyPivotSignupOptions {
  /**
   * ISO timestamp captured when the user ticked the combined 18+/ToS checkbox
   * on the OTP step (REK-2053). Must be a non-empty caller-supplied value —
   * this module never invents one.
   */
  termsAcceptedAtIso: string;
}

export const TERMS_AFFIRMATION_REQUIRED_MESSAGE =
  'Please confirm you are 18 or older and agree to the Terms before creating your account.';

/**
 * True when the caller supplied a real affirmation timestamp (non-empty after
 * trim). Empty / whitespace values must not reach consent persistence.
 */
export function hasCallerTermsAcceptance(termsAcceptedAtIso: string): boolean {
  return termsAcceptedAtIso.trim().length > 0;
}

// ── REK-2137: terms affirmation across the Google OAuth redirect ──────────

/**
 * localStorage key carrying the pivot checkbox's affirmation ISO across the
 * Google OAuth full-page redirect. Mirrors the scan funnel's
 * `reklaim_terms_accepted_at` handoff (useFastAuth) with a dedicated key so
 * the two flows can never consume each other's affirmation. Cleared on
 * consumption AND by `clearAuthLocalStorage` on sign-out.
 */
export const SSO_TERMS_ACCEPTED_AT_KEY = 'reklaim_sso_terms_accepted_at';

/**
 * Persist the checkbox affirmation right before starting the OAuth redirect.
 * The value MUST be the real user-affirmation ISO stamped by the pivot
 * screen's checkbox handler — never a value minted at call time.
 */
export function storeSsoTermsAcceptance(termsAcceptedAtIso: string): void {
  try {
    localStorage.setItem(SSO_TERMS_ACCEPTED_AT_KEY, termsAcceptedAtIso);
  } catch (err) {
    safeError('SSO signup: failed to persist terms affirmation:', err);
  }
}

/**
 * Read AND clear the persisted affirmation. Always clears — a stored
 * timestamp is single-use so it cannot leak into a later, unaffirmed signup.
 * Returns null for missing, empty, or unparseable values (the caller then
 * refuses to create the account rather than inventing a timestamp).
 */
export function consumeSsoTermsAcceptance(): string | null {
  try {
    const stored = localStorage.getItem(SSO_TERMS_ACCEPTED_AT_KEY);
    localStorage.removeItem(SSO_TERMS_ACCEPTED_AT_KEY);
    if (!stored) return null;
    const trimmed = stored.trim();
    if (!hasCallerTermsAcceptance(trimmed) || Number.isNaN(Date.parse(trimmed))) {
      return null;
    }
    return trimmed;
  } catch (err) {
    safeError('SSO signup: failed to read terms affirmation:', err);
    return null;
  }
}

/**
 * Best-effort post-verify persistence. Failures are logged and never fail the
 * signup (same posture as useFastAuth: "Don't block signup if consent
 * recording fails").
 */
async function persistPivotSignupRecords(
  userId: string,
  termsAcceptedAtIso: string,
): Promise<void> {
  const attribution = readStoredAttribution();
  const updates: Record<string, string> = {
    terms_accepted_at: termsAcceptedAtIso,
    ...profileUpdatesFromStoredAttribution(attribution),
  };
  try {
    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId);
    if (error) safeError('Pivot signup: profiles attribution/terms update failed:', error.message);
  } catch (err) {
    safeError('Pivot signup: profiles attribution/terms update threw:', err);
  }

  // Two independent consent audit rows — the combined checkbox attests both
  // (REK-1730 precedent: one-click signup records terms_of_service AND
  // age_verification). REK-1938: written via the record-consent edge
  // function (user_consents is service-role-only for writes); granted_at is
  // stamped server-side — the exact checkbox time lives on
  // profiles.terms_accepted_at above.
  for (const consentType of ['terms_of_service', 'age_verification'] as const) {
    try {
      await grantConsent(consentType);
    } catch (err) {
      safeError(`Pivot signup: ${consentType} consent grant failed:`, err);
    }
  }

  const referralCode = readAttributionReferralCode();
  if (referralCode) {
    try {
      const { data, error } = await supabase.functions.invoke('process-referral', {
        body: { referralCode },
      });
      if (error) {
        safeError('Pivot signup: process-referral failed:', error);
      } else if (data?.success) {
        analytics.trackEvent({
          name: 'referral_signup_completed',
          properties: { referring_user_id: data.referrerId || 'unknown' },
        });
      }
    } catch (err) {
      safeError('Pivot signup: process-referral threw:', err);
    }
  }
}

/**
 * Shared completion tail for BOTH pivot signup paths, entered once the Clerk
 * session has been requested for activation: wait out the SSO.2 webhook
 * race, then run the post-verify persistence + analytics with the
 * path-appropriate origin/method.
 */
async function completePivotSignupSession(options: {
  /** Trimmed, non-empty user-affirmation ISO (validated by the caller). */
  affirmedAtIso: string;
  /** Whether THIS signup applied implied cookie consent (fires consent_decision). */
  didAutoConsent: boolean;
  /** Which pivot path created the account — reported on the funnel events. */
  method: ClerkSignInMethod;
}): Promise<PivotVerifyResult> {
  const resolution = await waitForClerkSessionResolution(
    PIVOT_RESOLUTION_TIMEOUT_MS,
  );
  if (resolution === 'denied-locked') {
    return { status: 'error', message: ACCOUNT_LOCKED_MESSAGE, accountLocked: true };
  }
  if (resolution !== 'granted') {
    return {
      status: 'error',
      message: SESSION_VERIFY_FAILED_MESSAGE,
      accountLocked: false,
    };
  }

  if (options.didAutoConsent) {
    analytics.trackEvent({
      name: 'consent_decision',
      properties: {
        decision: 'analytics_only',
        variant: 'none',
        time_to_decision_ms: 0,
        trigger: 'signup_implied',
      },
    });
  }

  // Social-path emails are verified by the provider and arrive verified in
  // Clerk — the funnel event fires for both paths so the verified-email step
  // stays comparable across origins.
  analytics.trackEvent({ name: 'email_verified', properties: {} });

  const userId = getClerkAuthUser()?.id;
  if (userId) {
    await persistPivotSignupRecords(userId, options.affirmedAtIso);
  } else {
    // The resolution channel granted but the registry has no user — should be
    // unreachable; log so it surfaces in Sentry breadcrumbs if it ever happens.
    safeError('Pivot signup: session granted but no resolved user id');
  }

  const attribution = readStoredAttribution();
  const entrySource = resolveEntrySource(attribution);
  const experimentVariants = getExperimentVariantsMap();
  const authMethod =
    options.method === 'email' ? 'email_otp' : options.method;
  analytics.trackEvent({
    name: 'signup_completed',
    properties: {
      origin: options.method,
      auth_method: authMethod,
      entry_source: entrySource,
      experiment_variants: experimentVariants,
    },
  });
  analytics.trackEvent({
    name: 'registration_completed',
    properties: { method: options.method },
  });
  // App half of Meta CompleteRegistration via GTM (REK-2146). Consent-gated;
  // covers the default /auth LemonadeLogin pivot (email OTP + social SSO).
  pushCompleteRegistration({
    auth_method: authMethod,
    entry_source: entrySource,
    experiment_variants: experimentVariants,
    signup_campaign: attribution.campaign,
    signup_campaign_id: attribution.campaign_id,
  });

  return { status: 'complete' };
}

/**
 * Verify the pivot sign-up OTP and finish the account. Resolves 'complete'
 * once the session is granted and persistence has run; the caller then routes
 * to /onboarding (Track C name/DOB gate).
 */
export async function verifyPivotSignupCode(
  code: string,
  { termsAcceptedAtIso }: VerifyPivotSignupOptions,
): Promise<PivotVerifyResult> {
  // REK-2053 / SSO.14 D-B: refuse before OTP attempt or consent writes when
  // the caller did not supply a real affirmation timestamp. Do not invent one.
  const affirmedAt = termsAcceptedAtIso.trim();
  if (!hasCallerTermsAcceptance(affirmedAt)) {
    return {
      status: 'error',
      message: TERMS_AFFIRMATION_REQUIRED_MESSAGE,
      accountLocked: false,
    };
  }

  // Signup implies analytics-only consent (REK-3389; marketing needs explicit opt-in) — must be applied BEFORE the session
  // activates (see useFastAuth.verifyOtp for the full rationale). Applied via
  // the onBeforeSessionActivate callback so a failed OTP never persists it.
  let didAutoConsent = false;
  const applyImpliedConsent = () => {
    if (grantImpliedConsent('otp_login')) {
      didAutoConsent = true;
    }
  };

  const attempt = await attemptSignUpEmailCode(code, {
    onBeforeSessionActivate: applyImpliedConsent,
  });
  if (attempt.status === 'error') {
    return {
      status: 'error',
      message: attempt.error.message,
      accountLocked: attempt.error.accountLocked,
    };
  }

  return completePivotSignupSession({
    affirmedAtIso: affirmedAt,
    didAutoConsent,
    method: 'email',
  });
}

export interface FinalizeSsoPivotSignupOptions {
  /**
   * ISO timestamp of the user's real 18+/ToS affirmation on the pivot
   * screen, carried across the OAuth redirect via
   * `consumeSsoTermsAcceptance`. Must be non-empty — never invented.
   */
  termsAcceptedAtIso: string;
  /**
   * Whether the callback's onBeforeSignUpSessionActivate applied implied
   * cookie consent (grantImpliedConsent returned true) — fires the
   * consent_decision event, mirroring the OTP path.
   */
  didAutoConsent: boolean;
  /**
   * Clerk strategy that created the account (`oauth_google`, …). Reported on
   * signup_completed / registration_completed — never a bare provider slug.
   */
  method: `oauth_${string}`;
}

/**
 * REK-2137 / REK-2186: finish a social-created pivot account AFTER
 * `completeSocialSsoCallback` reports 'signed-up' (account created, session
 * activation requested, implied consent already applied before activation).
 * Runs the same completion tail as the OTP path: SSO.2 resolution wait +
 * post-verify persistence + funnel analytics with origin/method
 * `oauth_<provider>`. The caller routes to /onboarding on 'complete'
 * (onboarding_status stays `pending` — the name + DOB gate runs there).
 */
export async function finalizeSsoPivotSignup({
  termsAcceptedAtIso,
  didAutoConsent,
  method,
}: FinalizeSsoPivotSignupOptions): Promise<PivotVerifyResult> {
  // Defense-in-depth: the callback refuses the sign-up transfer without a
  // real affirmation, but this module owns the write contract — re-check so
  // no future caller can reach consent persistence with an empty timestamp.
  const affirmedAt = termsAcceptedAtIso.trim();
  if (!hasCallerTermsAcceptance(affirmedAt)) {
    return {
      status: 'error',
      message: TERMS_AFFIRMATION_REQUIRED_MESSAGE,
      accountLocked: false,
    };
  }

  return completePivotSignupSession({
    affirmedAtIso: affirmedAt,
    didAutoConsent,
    method,
  });
}
